import React, { useEffect, useRef, useState } from "react";
import { Button, InputNumber, Radio, Switch, Tooltip } from "antd";
import { CaretRightOutlined, CloseOutlined, InfoCircleOutlined, WarningFilled } from "@ant-design/icons";
import { findLast, map, uniq } from "lodash";
import {
  AICC_SANDBOX_FEEDBACK_VARIANT,
  AICC_SANDBOX_HINT_VARIANT,
  AICC_SANDBOX_LIMITS,
  AiccSandboxBaseScope,
  AiccSandboxMode,
} from "constants/aiccPromptSandbox";
import { AiccPromptModel } from "types/model/aiccPrompt";
import { useAiccLessonReviewScripts } from "../../hooks/useAiccLessonReviewScripts";
import { useAiccSandboxVariables } from "../../hooks/useAiccSandboxVariables";
import { useAiccSandboxRender } from "../../hooks/useAiccSandboxRender";
import VariableInputsForm from "./Variables/VariableInputsForm";
import LessonForm from "./Lesson/LessonForm";
import ChatThread from "./Conversation/ChatThread";
import ChatComposer from "./Conversation/ChatComposer";
import RunFacts from "./RunFacts";
import RenderedPrompts from "./RenderedPrompts";
import SupportingFunctions from "./SupportingFunctions";
import { useSandboxConversation } from "./Conversation/useSandboxConversation";
import { useSandboxActions } from "./Conversation/useSandboxActions";
import { useSandboxSupport } from "./SupportingFunctions/useSandboxSupport";
import { sandboxRequestBase } from "./sandboxRequest";
import { useSandboxInputs } from "./Variables/useSandboxInputsStore";
import { useSavedSinceRun } from "./useSavedSinceRun";
import { fixedVariableValues, inputsSummary, sandboxInputsRequest } from "./Variables/variableInputs";
import {
  ONBOARDING_VARIANTS,
  flowVariant,
  hasHint,
  hasLesson,
  hasOpenerVariant,
  hasSideActions,
  hasSupportingFunctions,
  hasVariantChoice,
  hasWrapUp,
} from "./sandboxScope";
import { EMPTY_LESSON, NO_LESSON, lessonRequest, lessonSummary } from "./Lesson/lessonRequest";
import { lessonHintVariables } from "./Lesson/lessonHint";
import { selectedScript } from "./Lesson/lessonScripts";
import { sandboxRequestLimits } from "./sandboxLimits";
import {
  ACTION_NO_ANSWER,
  DETAILS_CHANGED,
  DETAILS_UNSET,
  FIRST_CONVERSATION_HINT,
  FIRST_CONVERSATION_LABEL,
  LIMIT_BLOCKED,
  LIMIT_NEAR_ROUNDS,
  LIMIT_NEAR_TURNS,
  OPENER_CHANGED,
  HINT_LABEL,
  INPUTS_TITLE,
  LESSON_CHANGED,
  LESSON_TITLE,
  RESTART,
  RESTART_WITH_DETAILS,
  RESTART_WITH_LESSON,
  RESTART_WITH_OPENER,
  RESTART_WITH_SAVED,
  RESTART_WITH_VARIANT,
  ROUNDS_HINT,
  ROUNDS_LABEL,
  SANDBOX_EMPTY,
  SANDBOX_EMPTY_LESSON,
  SAVED_SINCE_RUN,
  START_CONVERSATION,
  ONBOARDING_VARIANT_LABEL,
  VARIANT_CHANGED,
  VARIANT_LABEL,
} from "./configs";
import styles from "./Sandbox.module.scss";

interface Props {
  languageId?: number;
  languageName?: string;
  mode: AiccSandboxMode;
  baseScope: AiccSandboxBaseScope;
  modelsById: Record<string, AiccPromptModel>;
  isOpen: boolean;
}

function Sandbox({ languageId, languageName, mode, baseScope, modelsById, isOpen }: Props) {
  const [onboardingVariant, setOnboardingVariant] = useState(ONBOARDING_VARIANTS[0]);
  const variant = flowVariant(mode, onboardingVariant);
  const { variables, isLoading: areVariablesLoading } = useAiccSandboxVariables(
    hasLesson(mode) ? undefined : languageId,
    mode
  );
  const { inputs: typedInputs, setInputs } = useSandboxInputs(languageId, mode, variables);
  const fixed = fixedVariableValues(variables, languageName);
  const inputs = hasLesson(mode) ? {} : { ...typedInputs, ...fixed };
  const [maxRounds, setMaxRounds] = useState(AICC_SANDBOX_LIMITS.defaultRounds);
  const [isFirstConversation, setIsFirstConversation] = useState(true);
  const [lessonState, setLessonState] = useState(EMPTY_LESSON);
  const { lessons, isLoading: isScriptsLoading } = useAiccLessonReviewScripts(
    hasLesson(mode) ? languageId : undefined,
    baseScope
  );
  const script = selectedScript(lessons, lessonState.level, lessonState.lesson, lessonState.scriptIndex);
  const lesson = hasLesson(mode) ? lessonRequest(lessonState, isScriptsLoading || !!script) : NO_LESSON;
  const {
    conversation,
    pendingMessage,
    lastRun,
    isRunning,
    isStarted,
    isWrapUpArmed,
    isWrapUpSent,
    areDetailsChanged,
    isOpenerChanged,
    isVariantChanged,
    isLessonChanged,
    cursor,
    runInputs,
    runLesson,
    start,
    send,
    rerun,
  } = useSandboxConversation({
    languageId,
    mode,
    variant,
    inputs,
    baseScope,
    maxRounds,
    isFirstConversation,
    lesson: lesson.lesson,
  });
  const [isRenderArmed, setIsRenderArmed] = useState(false);
  const { render, isLoading: isRendering, mutate: refreshRender } = useAiccSandboxRender(
    languageId,
    isRenderArmed && runInputs
      ? {
          mode,
          base_scope: baseScope,
          ...(hasLesson(mode) ? {} : sandboxInputsRequest(runInputs)),
        }
      : undefined
  );
  const lastAnswer = findLast(conversation.bubbles, { role: "user" })?.text;
  const {
    hint,
    isHinting,
    askForHint,
    dismissHint,
    corrections,
    correctingIndex,
    correct,
    clearActions,
  } = useSandboxActions({
    languageId,
    requestBase: () => sandboxRequestBase(inputs, baseScope),
    transcript: conversation.transcript,
    hint: hasLesson(mode)
      ? {
          variant: AICC_SANDBOX_HINT_VARIANT.lessonReview,
          variables: lessonHintVariables(script, cursor, lastAnswer),
        }
      : undefined,
  });
  const { feedback, isSummarising, summarise, words, isIdentifying, identify, clearSupport } = useSandboxSupport({
    languageId,
    requestBase: () => sandboxRequestBase(inputs, baseScope),
    transcript: conversation.transcript,
    lesson: hasLesson(mode) ? runLesson : undefined,
    feedbackVariant: hasLesson(mode)
      ? AICC_SANDBOX_FEEDBACK_VARIANT.lessonReview
      : AICC_SANDBOX_FEEDBACK_VARIANT.scenario,
  });
  const [areDetailsOpen, setAreDetailsOpen] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  const { savedSinceRun, clearSavedSinceRun } = useSavedSinceRun({
    languageId,
    mode,
    baseScope,
    promptKeys: uniq([...map(render?.prompts, "templateId"), ...map(lastRun?.promptsUsed, "templateId")]),
    isOpen,
    isStarted,
    onSaved: () => isRenderArmed && refreshRender(),
  });
  const hasSpoken = conversation.bubbles.some(({ role }) => role === "user");
  const limits = sandboxRequestLimits(conversation.transcript);

  useEffect(() => {
    const body = bodyRef.current;

    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [conversation.bubbles.length, isRunning]);

  const startConversation = () => {
    setAreDetailsOpen(false);
    start();
  };

  const restart = () => {
    clearActions();
    clearSupport();
    clearSavedSinceRun();
    rerun();
  };

  return (
    <div className={styles.sandbox}>
      <div className={isStarted ? styles.pinnedCapped : styles.pinned}>
        <button type="button" className={styles.detailsHeader} onClick={() => setAreDetailsOpen(!areDetailsOpen)}>
          <CaretRightOutlined rotate={areDetailsOpen ? 90 : 0} />
          <span className={styles.detailsTitle}>{hasLesson(mode) ? LESSON_TITLE : INPUTS_TITLE}</span>
          {!areDetailsOpen && (
            <span className={styles.detailsSummary}>
              {hasLesson(mode) ? lessonSummary(lessonState, script?.title) : inputsSummary(inputs) || DETAILS_UNSET}
            </span>
          )}
        </button>

        {areDetailsOpen &&
          (hasLesson(mode) ? (
            <LessonForm
              lesson={lessonState}
              lessons={lessons}
              isScriptsLoading={isScriptsLoading}
              isFixed={isRunning}
              onChange={setLessonState}
            />
          ) : (
            <VariableInputsForm
              variables={variables}
              inputs={inputs}
              fixedNames={Object.keys(fixed)}
              isDisabled={isRunning}
              onChange={setInputs}
            />
          ))}

        {hasVariantChoice(mode) && (
          <div className={styles.scopeRow}>
            <span className={styles.scopeLabel}>{VARIANT_LABEL}</span>
            <Radio.Group
              size="small"
              optionType="button"
              value={onboardingVariant}
              disabled={isRunning}
              options={map(ONBOARDING_VARIANTS, (key) => ({ label: ONBOARDING_VARIANT_LABEL[key], value: key }))}
              onChange={(event) => setOnboardingVariant(event.target.value)}
            />
          </div>
        )}

        {hasOpenerVariant(mode) && (
          <div className={styles.scopeRow}>
            <span className={styles.scopeLabel}>{FIRST_CONVERSATION_LABEL}</span>
            <Switch
              size="small"
              aria-label={FIRST_CONVERSATION_LABEL}
              checked={isFirstConversation}
              disabled={isRunning}
              onChange={setIsFirstConversation}
            />
            <Tooltip title={FIRST_CONVERSATION_HINT}>
              <InfoCircleOutlined className={styles.scopeInfo} />
            </Tooltip>
          </div>
        )}

        {hasWrapUp(mode) && (
          <div className={styles.scopeRow}>
            <span className={styles.scopeLabel}>{ROUNDS_LABEL}</span>
            <InputNumber
              size="small"
              min={AICC_SANDBOX_LIMITS.minRounds}
              max={AICC_SANDBOX_LIMITS.maxRounds}
              value={maxRounds}
              disabled={hasSpoken}
              onChange={(value) => setMaxRounds(Number(value) || AICC_SANDBOX_LIMITS.defaultRounds)}
            />
            <Tooltip title={ROUNDS_HINT(AICC_SANDBOX_LIMITS.wrapupGrace)}>
              <InfoCircleOutlined className={styles.scopeInfo} />
            </Tooltip>
          </div>
        )}

        {isStarted && (
          <div className={styles.scopeRow}>
            <RenderedPrompts render={render} isLoading={isRendering} onOpen={() => setIsRenderArmed(true)} />
          </div>
        )}
      </div>

      {!!savedSinceRun.length && (
        <div className={styles.applyRow}>
          <span className={styles.applyText}>
            <WarningFilled /> {SAVED_SINCE_RUN(savedSinceRun)}
          </span>
          <Button type="primary" shape="round" size="small" loading={isRunning} onClick={restart}>
            {RESTART_WITH_SAVED}
          </Button>
        </div>
      )}

      {areDetailsChanged && (
        <div className={styles.applyRow}>
          <span className={styles.applyText}>
            <WarningFilled /> {DETAILS_CHANGED}
          </span>
          <Button type="primary" shape="round" size="small" loading={isRunning} onClick={restart}>
            {RESTART_WITH_DETAILS}
          </Button>
        </div>
      )}

      {isOpenerChanged && (
        <div className={styles.applyRow}>
          <span className={styles.applyText}>
            <WarningFilled /> {OPENER_CHANGED}
          </span>
          <Button type="primary" shape="round" size="small" loading={isRunning} onClick={restart}>
            {RESTART_WITH_OPENER}
          </Button>
        </div>
      )}

      {isVariantChanged && (
        <div className={styles.applyRow}>
          <span className={styles.applyText}>
            <WarningFilled /> {VARIANT_CHANGED}
          </span>
          <Button type="primary" shape="round" size="small" loading={isRunning} onClick={restart}>
            {RESTART_WITH_VARIANT}
          </Button>
        </div>
      )}

      {isLessonChanged && (
        <div className={styles.applyRow}>
          <span className={styles.applyText}>
            <WarningFilled /> {LESSON_CHANGED}
          </span>
          <Button type="primary" shape="round" size="small" loading={isRunning} onClick={restart}>
            {RESTART_WITH_LESSON}
          </Button>
        </div>
      )}

      {!!lesson.error && (
        <div className={styles.limit}>
          <WarningFilled className={styles.limitIcon} />
          {lesson.error}
        </div>
      )}

      {(limits.isBlocked || limits.isNear) && (
        <div className={styles.limit}>
          <WarningFilled className={styles.limitIcon} />
          {limits.isBlocked
            ? LIMIT_BLOCKED
            : hasWrapUp(mode)
            ? LIMIT_NEAR_ROUNDS(lastRun?.roundCount ?? 0, limits.roundsLeft)
            : LIMIT_NEAR_TURNS(limits.turns, limits.turnsLeft)}
        </div>
      )}

      <div ref={bodyRef} className={styles.body}>
        {isStarted || isRunning ? (
          <ChatThread
            bubbles={conversation.bubbles}
            isThinking={isRunning}
            pendingUserText={pendingMessage}
            hasCheck={hasSideActions(mode)}
            corrections={corrections}
            correctingIndex={correctingIndex}
            onCorrect={correct}
          />
        ) : (
          <div className={styles.empty}>{hasLesson(mode) ? SANDBOX_EMPTY_LESSON : SANDBOX_EMPTY}</div>
        )}
      </div>

      <div className={styles.pinnedFooter}>
        {hint !== undefined && (
          <div className={styles.hint}>
            <span className={styles.hintLabel}>{HINT_LABEL}</span>
            <span>{hint ?? ACTION_NO_ANSWER}</span>
            <Button type="link" className={styles.hintDismiss} onClick={dismissHint}>
              <CloseOutlined />
            </Button>
          </div>
        )}

        {isStarted ? (
          <ChatComposer
            isEnded={conversation.isEnded}
            endReason={lastRun?.endReason}
            isRunning={isRunning}
            isHinting={isHinting}
            hasHint={hasHint(mode)}
            hasWrapUp={hasWrapUp(mode)}
            isBlocked={limits.isBlocked || !!lesson.error}
            isWrapUpArmed={isWrapUpArmed}
            isWrapUpSent={isWrapUpSent}
            onHint={askForHint}
            onSend={send}
          />
        ) : (
          <div className={styles.startRow}>
            <Button
              type="primary"
              shape="round"
              disabled={isRunning || areVariablesLoading || isScriptsLoading || !!lesson.error}
              onClick={startConversation}
            >
              {START_CONVERSATION}
            </Button>
          </div>
        )}

        {!!lastRun && (
          <RunFacts
            facts={lastRun}
            settings={hasLesson(mode) ? undefined : lastRun.effective}
            modelsById={modelsById}
            flowsUsed={hasLesson(mode) ? lastRun.flowsUsed : undefined}
            rounds={hasWrapUp(mode) ? { count: lastRun.roundCount, isArmed: isWrapUpArmed } : undefined}
          />
        )}

        {isStarted && (
          <div className={styles.startRow}>
            <Button type="link" className={styles.restartButton} disabled={isRunning} onClick={restart}>
              {RESTART}
            </Button>

            {hasSupportingFunctions(mode) && (
              <SupportingFunctions
                hasConversation={hasSpoken}
                feedback={feedback}
                isSummarising={isSummarising}
                onSummarise={summarise}
                words={words}
                isIdentifying={isIdentifying}
                onIdentify={identify}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Sandbox;
